import {useContext, useMemo} from 'react'
import {Link, useLocation} from 'react-router-dom'

import PropTypes from 'prop-types'

import Context from '@s-ui/react-context'

import {setNavigationState} from '../../helpers/navigationStateCache.ts'
import {
  ADS_DETAIL_COCHES_REGEX,
  ADS_DETAIL_COCHES_SUBSCRIPTION_REGEX,
  ADS_DETAIL_MOTOS_REGEX,
  APP_NAMES,
  EXACT_REACT_ROUTES,
  NEWS_DETAIL_REGEX,
  PARTIAL_REACT_ROUTES,
  USER_FAVOURITES_REGEX
} from '../../config/index.js'

const LinkFactory = ({href, children, state, isExternal, onClick, ...props}) => {
  const {domain} = useContext(Context)
  const {search} = useLocation()

  const config = domain.get('config')
  const {NEWS_CATEGORY_PATH_PATTERN} = config.get('page')
  const {categoriesUrls} = config.get('editorial')

  const categories = useMemo(
    () => categoriesUrls.filter(Boolean).map(cat => NEWS_CATEGORY_PATH_PATTERN.replace(':category', cat)),
    []
  )

  const exactReactRoutes = useMemo(() => [...EXACT_REACT_ROUTES, ...categories], [])

  const isCochesAdDetailsPage = Boolean(href?.match(ADS_DETAIL_COCHES_REGEX))
  const isMotosAdDetailsPage = Boolean(href?.match(ADS_DETAIL_MOTOS_REGEX))

  const isCochesNewsDetail = Boolean(href?.match(NEWS_DETAIL_REGEX[APP_NAMES.COCHES]))
  const isMotosNewsDetail = Boolean(href?.match(NEWS_DETAIL_REGEX[APP_NAMES.MOTOS]))

  const isNewsDetail = isCochesNewsDetail || isMotosNewsDetail

  const isAdsDetailSemanticUrl = isCochesAdDetailsPage || isMotosAdDetailsPage

  const isCochesRentingAdDetailsPage = Boolean(href?.match(ADS_DETAIL_COCHES_SUBSCRIPTION_REGEX))

  const isCochesUserFavorites = Boolean(href?.match(USER_FAVOURITES_REGEX))

  const isExactReactRoute = useMemo(() => exactReactRoutes.some(route => route === href), [href])
  const isReactRouteWithParams = useMemo(() => PARTIAL_REACT_ROUTES.some(link => href?.startsWith(link)), [href])

  const isStockSemanticUrl = Boolean(href?.match(/concesionario.*/))

  const isCmpAuth = Boolean(search?.match(/cmpAuth=true/))

  /**
   * IF href match with exact react route
   * OR href is the beginning of a react route
   */
  const isReactLink =
    isExactReactRoute ||
    isReactRouteWithParams ||
    isAdsDetailSemanticUrl ||
    isStockSemanticUrl ||
    isNewsDetail ||
    isCochesRentingAdDetailsPage ||
    isCochesUserFavorites

  // The destination's loader reads `state` back from the cache — RR7 keeps it out of
  // `LoaderFunctionArgs` (see helpers/navigationStateCache.ts).
  const handleClick = event => {
    setNavigationState(href, state)
    onClick?.(event)
  }

  return isReactLink && !isExternal && !isCmpAuth ? (
    <Link to={href} {...props} state={state} onClick={handleClick}>
      {children}
    </Link>
  ) : (
    <a href={href} {...props}>
      {children}
    </a>
  )
}

LinkFactory.displayName = 'LinkFactory'

LinkFactory.propTypes = {
  children: PropTypes.node.isRequired,
  href: PropTypes.string.isRequired,
  isExternal: PropTypes.bool,
  onClick: PropTypes.func,
  state: PropTypes.object
}

export default LinkFactory
